import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import ModalBase from '../components/ModalBase';

// Checklist diario/semanal da equipe de TI.
//
// A pagina tem tres visoes:
//   - "Hoje"      : itens devidos hoje, com checkbox para marcar concluido.
//                   Enquanto houver pendentes, o Sidebar mostra badge laranja.
//   - "Itens"     : catalogo dos checkpoints (CRUD flexivel — a equipe adiciona
//                   novos itens sem precisar mexer no codigo).
//   - "Historico" : lista das execucoes registradas para auditoria.

const DIAS_SEMANA = [
  { valor: 0, curto: 'Dom', longo: 'Domingo' },
  { valor: 1, curto: 'Seg', longo: 'Segunda-feira' },
  { valor: 2, curto: 'Ter', longo: 'Terca-feira' },
  { valor: 3, curto: 'Qua', longo: 'Quarta-feira' },
  { valor: 4, curto: 'Qui', longo: 'Quinta-feira' },
  { valor: 5, curto: 'Sex', longo: 'Sexta-feira' },
  { valor: 6, curto: 'Sab', longo: 'Sabado' },
];

function formatDT(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatData(d) {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(`${d}T00:00:00`) : new Date(d);
  return dt.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function ModalItem({ item, onClose, onSalvo }) {
  const editando = !!item;
  const [form, setForm] = useState({
    titulo: item?.titulo || '',
    descricao: item?.descricao || '',
    tipo: item?.tipo || 'diario',
    dia_semana: item?.dia_semana ?? 1,
    ordem: item?.ordem ?? 0,
    ativo: item?.ativo !== 0 && item?.ativo !== false,
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim() || null,
        tipo: form.tipo,
        dia_semana: form.tipo === 'semanal' ? Number(form.dia_semana) : null,
        ordem: Number(form.ordem) || 0,
        ativo: !!form.ativo,
      };
      if (editando) {
        await axios.put(`${API}/checklist/itens/${item.id}`, payload);
      } else {
        await axios.post(`${API}/checklist/itens`, payload);
      }
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao salvar item.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo={editando ? 'Editar item' : 'Novo item de checklist'} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>Titulo</label>
          <input
            type="text" required minLength={3} maxLength={200}
            value={form.titulo}
            onChange={(e) => set('titulo', e.target.value)}
            style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)' }}
            placeholder="Ex.: Verificar status dos servidores"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>Descricao (opcional)</label>
          <textarea
            rows={3}
            value={form.descricao}
            onChange={(e) => set('descricao', e.target.value)}
            style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)', resize: 'vertical' }}
            placeholder="Detalhe o que precisa ser feito"
          />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>Frequencia</label>
            <select
              value={form.tipo}
              onChange={(e) => set('tipo', e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)' }}
            >
              <option value="diario">Diario (todo dia)</option>
              <option value="semanal">Semanal (dia especifico)</option>
            </select>
          </div>

          {form.tipo === 'semanal' && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>Dia da semana</label>
              <select
                value={form.dia_semana}
                onChange={(e) => set('dia_semana', e.target.value)}
                style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)' }}
              >
                {DIAS_SEMANA.map((d) => (
                  <option key={d.valor} value={d.valor}>{d.longo}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>Ordem</label>
            <input
              type="number"
              value={form.ordem}
              onChange={(e) => set('ordem', e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)' }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', paddingBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => set('ativo', e.target.checked)}
            />
            <span>Ativo</span>
          </label>
        </div>

        {erro && (
          <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-sm" onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--portal-border)' }}>
            Cancelar
          </button>
          <button type="submit" disabled={salvando} className="btn btn-sm" style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}>
            {salvando ? 'Salvando...' : editando ? 'Salvar alteracoes' : 'Criar item'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

function ModalConcluir({ item, onClose, onConcluido }) {
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    try {
      await axios.post(`${API}/checklist/itens/${item.id}/concluir`, {
        observacao: observacao.trim() || null,
      });
      onConcluido();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao concluir.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo="Concluir item" onClose={onClose}>
      <form onSubmit={submit}>
        <p style={{ margin: '0 0 12px', color: 'var(--portal-muted)' }}>
          <strong>{item.titulo}</strong>
        </p>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '.85rem', marginBottom: 4 }}>
            Observacao (opcional)
          </label>
          <textarea
            rows={3}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--portal-border)', resize: 'vertical' }}
            placeholder="Alguma nota que valha registrar (opcional)"
          />
        </div>
        {erro && (
          <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-sm" onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--portal-border)' }}>
            Cancelar
          </button>
          <button type="submit" disabled={salvando} className="btn btn-sm" style={{ background: '#28a745', color: '#fff', border: 'none' }}>
            {salvando ? 'Salvando...' : 'Marcar como concluido'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

export default function Checklist() {
  const [aba, setAba] = useState('hoje'); // hoje | itens | historico

  const [hoje, setHoje] = useState([]);
  const [itens, setItens] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [modalItem, setModalItem] = useState(null); // { editando? }
  const [modalConcluir, setModalConcluir] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [h, i, hist] = await Promise.all([
        axios.get(`${API}/checklist/hoje`),
        axios.get(`${API}/checklist/itens`),
        axios.get(`${API}/checklist/historico?dias=30`),
      ]);
      setHoje(h.data?.data || []);
      setItens(i.data?.data || []);
      setHistorico(hist.data?.data || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Nao foi possivel carregar o checklist.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const pendentes = useMemo(() => hoje.filter((i) => !i.concluido).length, [hoje]);
  const concluidos = useMemo(() => hoje.filter((i) => i.concluido).length, [hoje]);

  const desfazer = async (item) => {
    const confirmar = await Swal.fire({
      title: 'Desmarcar conclusao?',
      text: `"${item.titulo}" voltara para a lista de pendentes de hoje.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, desmarcar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmar.isConfirmed) return;
    try {
      await axios.delete(`${API}/checklist/itens/${item.id}/concluir`);
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao desfazer.', 'error');
    }
  };

  const excluirItem = async (item) => {
    const confirmar = await Swal.fire({
      title: 'Excluir item?',
      html: `"<strong>${item.titulo}</strong>" e todo o historico associado serao removidos.<br>Esta acao nao pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!confirmar.isConfirmed) return;
    try {
      await axios.delete(`${API}/checklist/itens/${item.id}`);
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao excluir.', 'error');
    }
  };

  const toggleAtivo = async (item) => {
    try {
      await axios.put(`${API}/checklist/itens/${item.id}`, {
        titulo: item.titulo,
        descricao: item.descricao,
        tipo: item.tipo,
        dia_semana: item.dia_semana,
        ordem: item.ordem,
        ativo: !item.ativo,
      });
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao atualizar.', 'error');
    }
  };

  const abaButton = (chave, label, icon) => (
    <button
      type="button"
      onClick={() => setAba(chave)}
      className="btn btn-sm"
      style={{
        background: aba === chave ? '#2A8A9C' : 'transparent',
        color: aba === chave ? '#fff' : 'var(--portal-text)',
        border: aba === chave ? 'none' : '1px solid var(--portal-border)',
      }}
    >
      <i className={`bi ${icon} me-1`} /> {label}
    </button>
  );

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Checklist da equipe</h1>
          <p>Checkpoints diarios e semanais que a equipe de TI precisa cumprir. Cada conclusao fica registrada.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {pendentes > 0 && (
            <span style={{
              background: 'rgba(253,126,20,0.15)', color: '#fd7e14',
              border: '1px solid rgba(253,126,20,0.4)', borderRadius: 20,
              padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600,
            }}>
              <i className="bi bi-bell-fill me-1" />
              {pendentes} pendente{pendentes !== 1 ? 's' : ''} hoje
            </span>
          )}
          {pendentes === 0 && hoje.length > 0 && (
            <span style={{
              background: 'rgba(40,167,69,0.15)', color: '#28a745',
              border: '1px solid rgba(40,167,69,0.4)', borderRadius: 20,
              padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600,
            }}>
              <i className="bi bi-check-circle-fill me-1" />
              Tudo em dia
            </span>
          )}
        </div>
      </section>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {abaButton('hoje', `Hoje (${concluidos}/${hoje.length})`, 'bi-calendar-check')}
        {abaButton('itens', `Itens (${itens.length})`, 'bi-list-check')}
        {abaButton('historico', 'Historico', 'bi-clock-history')}
      </div>

      {erro && (
        <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>
          {erro}
          <button type="button" onClick={() => setErro('')} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      {carregando ? (
        <section className="content-card">
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando...</p>
          </div>
        </section>
      ) : aba === 'hoje' ? (
        <section className="content-card">
          {hoje.length === 0 ? (
            <div className="empty-state">
              <strong>Nao ha checkpoints para hoje.</strong>
              <span>Crie itens diarios ou semanais na aba &quot;Itens&quot;.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hoje.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '12px 14px', borderRadius: 10,
                    border: `1px solid ${item.concluido ? 'rgba(40,167,69,0.4)' : 'var(--portal-border)'}`,
                    background: item.concluido ? 'rgba(40,167,69,0.06)' : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => item.concluido ? desfazer(item) : setModalConcluir(item)}
                    title={item.concluido ? 'Desmarcar' : 'Marcar como concluido'}
                    style={{
                      width: 24, height: 24, borderRadius: '50%',
                      border: `2px solid ${item.concluido ? '#28a745' : 'var(--portal-border)'}`,
                      background: item.concluido ? '#28a745' : 'transparent',
                      cursor: 'pointer', color: '#fff', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginTop: 2,
                    }}
                    aria-pressed={item.concluido}
                  >
                    {item.concluido && <i className="bi bi-check-lg" style={{ fontSize: '.9rem' }} />}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong style={{ textDecoration: item.concluido ? 'line-through' : 'none', color: item.concluido ? 'var(--portal-muted)' : 'inherit' }}>
                        {item.titulo}
                      </strong>
                      <span style={{
                        fontSize: '.72rem', padding: '2px 8px', borderRadius: 8,
                        background: item.tipo === 'diario' ? 'rgba(42,138,156,0.15)' : 'rgba(111,66,193,0.15)',
                        color: item.tipo === 'diario' ? '#2A8A9C' : '#6f42c1',
                        border: `1px solid ${item.tipo === 'diario' ? 'rgba(42,138,156,0.4)' : 'rgba(111,66,193,0.4)'}`,
                      }}>
                        {item.tipo === 'diario' ? 'Diario' : `Semanal · ${DIAS_SEMANA[item.dia_semana]?.curto || '?'}`}
                      </span>
                    </div>
                    {item.descricao && (
                      <div style={{ fontSize: '.85rem', color: 'var(--portal-muted)', marginTop: 4 }}>
                        {item.descricao}
                      </div>
                    )}
                    {item.execucao && (
                      <div style={{ fontSize: '.75rem', color: 'var(--portal-muted)', marginTop: 4 }}>
                        <i className="bi bi-check-circle me-1" />
                        Concluido por <strong>{item.execucao.concluido_por || '—'}</strong> em {formatDT(item.execucao.concluido_em)}
                        {item.execucao.observacao && <> · {item.execucao.observacao}</>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : aba === 'itens' ? (
        <section className="content-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: '.9rem', color: 'var(--portal-muted)' }}>
              Ative, edite ou crie novos checkpoints. Itens inativos nao aparecem na lista de hoje.
            </div>
            <button
              type="button" className="btn btn-sm"
              onClick={() => setModalItem({})}
              style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
            >
              <i className="bi bi-plus-lg me-1" /> Novo item
            </button>
          </div>

          {itens.length === 0 ? (
            <div className="empty-state">
              <strong>Nenhum item cadastrado ainda.</strong>
              <span>Comece criando um novo checkpoint.</span>
            </div>
          ) : (
            <div className="table-shell">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Titulo</th>
                    <th>Frequencia</th>
                    <th style={{ textAlign: 'center' }}>Ordem</th>
                    <th style={{ textAlign: 'center' }}>Ativo</th>
                    <th style={{ textAlign: 'right' }}>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item) => (
                    <tr key={item.id} style={{ opacity: item.ativo ? 1 : 0.55 }}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{item.titulo}</div>
                        {item.descricao && (
                          <div style={{ fontSize: '.78rem', color: 'var(--portal-muted)' }}>
                            {item.descricao}
                          </div>
                        )}
                      </td>
                      <td>
                        {item.tipo === 'diario'
                          ? <span>Diario</span>
                          : <span>Semanal · {DIAS_SEMANA[item.dia_semana]?.longo || '—'}</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{item.ordem}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => toggleAtivo(item)}
                          title={item.ativo ? 'Desativar' : 'Reativar'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: item.ativo ? '#28a745' : 'var(--portal-muted)' }}
                        >
                          <i className={`bi ${item.ativo ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
                        </button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button" className="btn btn-sm"
                          onClick={() => setModalItem(item)}
                          style={{ background: 'transparent', border: '1px solid var(--portal-border)', marginRight: 6 }}
                          title="Editar"
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button" className="btn btn-sm"
                          onClick={() => excluirItem(item)}
                          style={{ background: 'transparent', border: '1px solid rgba(220,53,69,0.4)', color: '#dc3545' }}
                          title="Excluir"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="content-card">
          {historico.length === 0 ? (
            <div className="empty-state">
              <strong>Nenhum registro nos ultimos 30 dias.</strong>
              <span>Assim que a equipe comecar a concluir itens, o historico aparece aqui.</span>
            </div>
          ) : (
            <div className="table-shell">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Item</th>
                    <th>Tipo</th>
                    <th>Concluido por</th>
                    <th>Em</th>
                    <th>Observacao</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((h) => (
                    <tr key={h.execucao_id}>
                      <td>{formatData(h.data_referencia)}</td>
                      <td style={{ fontWeight: 600 }}>{h.titulo}</td>
                      <td>{h.tipo === 'diario' ? 'Diario' : `Semanal · ${DIAS_SEMANA[h.dia_semana]?.curto || '—'}`}</td>
                      <td>{h.concluido_por || '—'}</td>
                      <td>{formatDT(h.concluido_em)}</td>
                      <td style={{ color: 'var(--portal-muted)' }}>{h.observacao || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {modalItem && (
        <ModalItem
          item={modalItem.id ? modalItem : null}
          onClose={() => setModalItem(null)}
          onSalvo={() => { setModalItem(null); carregar(); }}
        />
      )}
      {modalConcluir && (
        <ModalConcluir
          item={modalConcluir}
          onClose={() => setModalConcluir(null)}
          onConcluido={() => { setModalConcluir(null); carregar(); }}
        />
      )}
    </div>
  );
}
